/**
 * AI Recruit - Authentication Store Slice
 * Redux Toolkit slice for authentication state management
 */

import { createSlice } from '@reduxjs/toolkit'

export interface User {
  id: string
  email: string
  username: string
  firstName: string
  lastName: string
  fullName: string
  role: string
  organizationId: string
  avatar?: string
  permissions: string[]
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('ai_recruit_token'),
  refreshToken: localStorage.getItem('ai_recruit_refresh_token'),
  isAuthenticated: false,
  isLoading: true,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true
      state.error = null
    },
    loginSuccess: (state, action: { payload: { user: User; token: string; refreshToken?: string } }) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken || state.refreshToken
      state.isAuthenticated = true
      state.isLoading = false
      state.error = null
      localStorage.setItem('ai_recruit_token', action.payload.token)
      if (action.payload.refreshToken) {
        localStorage.setItem('ai_recruit_refresh_token', action.payload.refreshToken)
      }
    },
    loginFailure: (state, action: { payload: string }) => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.isLoading = false
      state.error = action.payload
      localStorage.removeItem('ai_recruit_token')
      localStorage.removeItem('ai_recruit_refresh_token')
    },
    logout: (state) => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.isLoading = false
      state.error = null
      localStorage.removeItem('ai_recruit_token')
      localStorage.removeItem('ai_recruit_refresh_token')
    },
    clearError: (state) => {
      state.error = null
    },
    setLoading: (state, action: { payload: boolean }) => {
      state.isLoading = action.payload
    },
    updateUser: (state, action: { payload: Partial<User> }) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
  },
})

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  setLoading,
  updateUser,
} = authSlice.actions

export default authSlice.reducer